import inputMasks from '@/lib/input-masks';

import { CountryCode, CountryCodeValue } from '@/constants/country-codes';
import { Currency } from '@/constants/currency';
import Region from '@/constants/regions';
import RoutePath from '@/constants/route-path';
import { LocaleProps } from '@/models/props/LocalizationProps';
import { EnvironmentService } from '@/services/EnvironmentService';
import {
    CountryInstanceValidators,
    GermanyInstanceValidators,
    PolandInstanceValidators
} from '@/validators/countryInstanceValidators';
import { DefaultInstanceValidators } from '@/validators/countryInstanceValidators/DefaultInstanceValidators';
import { HungaryInstanceValidators } from '@/validators/countryInstanceValidators/HungaryInstanceValidators';
import { RomaniaInstanceValidators } from '@/validators/countryInstanceValidators/RomaniaInstanceValidators';

function getPrefixedAccountNumber(accountNumber: string) {
    return getAppCountryCode().concat('-', accountNumber);
}

function getInstanceCurrency() {
    const appCountryCode = getAppCountryCode().toUpperCase() as CountryCodeValue;
    const currency = Currency[appCountryCode];
    if (!currency) {
        console.warn(`Currency for ${appCountryCode} instance is not defined`); // eslint-disable-line no-console
        return '';
    }
    return currency;
}

function getInstanceInputMasks() {
    const appCountryCode = getAppCountryCode().toUpperCase() as CountryCodeValue;
    const instanceInputMasks = inputMasks[appCountryCode];
    if (!instanceInputMasks) {
        throw new Error(`Input masks for ${appCountryCode} instance are not defined`);
    }
    return instanceInputMasks;
}

function getInstanceRegion(): string {
    const instanceCountryCode = getAppCountryCode().toLowerCase() as LocaleProps;
    return Region[instanceCountryCode];
}

function isGermanInstance() {
    return getAppCountryCode() === CountryCode.DE;
}

function isRomaniaInstance() {
    return getAppCountryCode() === CountryCode.RO;
}

function getAdultAge(): number {
    const appCountryCode = getAppCountryCode().toUpperCase();

    switch (appCountryCode) {
        case CountryCode.DE:
            return 18;
        case CountryCode.PL:
            return 18;
        case CountryCode.HU:
            return 18;
        case CountryCode.RO:
            return 18;
        default:
            throw new Error('Adult age must be defined');
    }
}

function getAppCountryCode(): string {
    const instanceCountryCode = process.env.NEXT_PUBLIC_INSTANCE_COUNTRY_CODE;
    if (!instanceCountryCode) {
        throw new Error('Instance country code is not defined. Check environment configuration');
    }
    return instanceCountryCode;
}

function getContactEmailAddress(): string {
    const contactEmailAddress = process.env.NEXT_PUBLIC_CONTACT_EMAIL_ADDRESS;
    if (!contactEmailAddress) {
        throw new Error('Contact email address is not defined. Check environment configuration');
    }
    return contactEmailAddress;
}

function getClubRegulationsFileLink() {
    const appCountryCode = getAppCountryCode();
    switch (appCountryCode) {
        case CountryCode.PL:
            return `/dokumenty/regulamin_klubu.pdf`;
        case CountryCode.DE:
            return `/documents/betterstyle_clubordnung.pdf`;
        case CountryCode.HU:
            return `/dokumentumok/klubszabalyzat.pdf`;
        case CountryCode.RO:
            return `/documente/regulamentul_clubului.pdf`;
        default:
            return '';
    }
}

function getStoreRegulationsFileLink() {
    const appCountryCode = getAppCountryCode();
    switch (appCountryCode) {
        case CountryCode.PL:
            return `/dokumenty/regulamin_sklepu.pdf`;
        case CountryCode.DE:
            return `/documents/betterstyle_shop_bedingungen.pdf`;
        case CountryCode.HU:
            return `/dokumentumok/webaruhaz_szabalyzata.pdf`;
        case CountryCode.RO:
            return `/documente/regulamentul_magazinului.pdf`;
        default:
            return '';
    }
}

function getCompanyClubRegulationsFileLink() {
    const appCountryCode = getAppCountryCode();
    switch (appCountryCode) {
        case CountryCode.PL:
            return `/dokumenty/regulamin_klubu_dla_firm.pdf`;
        case CountryCode.DE:
            return `/documents/besonderen_bedingungen_des_Betterstyle_Clubs.pdf`;
        case CountryCode.HU:
            return `/dokumentumok/klubszabalyzat.pdf`;
        case CountryCode.RO:
            return `/dokumentumok/klubszabalyzat.pdf`;
        default:
            return '';
    }
}

function getClubPrivacyPolicyFileLink() {
    const appCountryCode = getAppCountryCode();
    switch (appCountryCode) {
        case CountryCode.PL:
            return `/dokumenty/polityka_prywatnosci.pdf`;
        case CountryCode.DE:
            return `/documents/datenschutzerklarung.pdf`;
        case CountryCode.HU:
            return `/dokumentumok/adatvedelmi_nyilatkozat.pdf`;
        case CountryCode.RO:
            return `/documente/politica_de_confidentialitate_a_clubului.pdf`;
        default:
            return '';
    }
}

function getStorePrivacyPolicyFileLink() {
    const appCountryCode = getAppCountryCode();
    switch (appCountryCode) {
        case CountryCode.PL:
            return `/dokumenty/polityka_prywatnosci_sklepu.pdf`;
        case CountryCode.DE:
            return `/documents/datenschutzerklarung.pdf`;
        case CountryCode.HU:
            return `/dokumentumok/webaruhaz_adatvedelmi_nyilatkozat.pdf`;
        case CountryCode.RO:
            return `/documente/politica_de_confidențialitate_a_magazinului.pdf`;
        default:
            return '';
    }
}

function getMainLanguage(): string {
    const language = process.env.NEXT_PUBLIC_APP_MAIN_LANGUAGE || '';
    if (!language) {
        throw Error(
            'Default language is not set. Check NEXT_PUBLIC_APP_MAIN_LANGUAGE key in environments configuration.'
        );
    }
    return language;
}

function getLanguages(): string[] {
    const mainLanguage = getMainLanguage();
    const languagesString = EnvironmentService.getI18nAdditionalLanguages() || '';
    const languages = languagesString.split(',').filter((language) => language);
    return [mainLanguage, ...languages];
}

function getSellZone() {
    const sellZone = process.env.NEXT_PUBLIC_SELL_ZONE;
    if (!sellZone) {
        throw Error(
            'Sell zone is not set. Check NEXT_PUBLIC_SELL_ZONE key in environments configuration.'
        );
    }

    return sellZone;
}

function getSponsorLink(sponsorNumber: string): string {
    return `${process.env.NEXT_PUBLIC_CANNONICAL_URL}${RoutePath.Join}/${sponsorNumber}`;
}

function getInformationObligationFileLink() {
    const appCountryCode = getAppCountryCode();
    switch (appCountryCode) {
        case CountryCode.PL:
            return `/dokumenty/obowiazek_informacyjny.pdf`;
        case CountryCode.DE:
            return `/documents/informationspflicht.pdf`;
        case CountryCode.HU:
            return `/dokumentumok/tajekoztatasi_kotelezettseg.pdf`;
        case CountryCode.RO:
            return `/documente/obligatia_de_informare.pdf`;
        default:
            return '';
    }
}

function getInstanceCountryValidators(): CountryInstanceValidators {
    const appCountryCode = getAppCountryCode();
    switch (appCountryCode) {
        case CountryCode.PL:
            return new PolandInstanceValidators();
        case CountryCode.DE:
            return new GermanyInstanceValidators();
        case CountryCode.HU:
            return new HungaryInstanceValidators();
        case CountryCode.RO:
            return new RomaniaInstanceValidators();
        default:
            return new DefaultInstanceValidators();
    }
}

function getPhonePrefix(): string {
    const instanceCountryCode = getAppCountryCode();
    switch (instanceCountryCode) {
        case CountryCode.PL:
            return '+48';
        case CountryCode.DE:
            return '+49';
        case CountryCode.HU:
            return '+36';
        case CountryCode.RO:
            return '+40';
        default:
            return '';
    }
}

export const AppService = {
    getAdultAge,
    getAppCountryCode,
    getCompanyClubRegulationsFileLink,
    getContactEmailAddress,
    getClubRegulationsFileLink,
    getStoreRegulationsFileLink,
    getClubPrivacyPolicyFileLink,
    getStorePrivacyPolicyFileLink,
    getInstanceCurrency,
    getInstanceInputMasks,
    getInstanceRegion,
    getSponsorLink,
    getInformationObligationFileLink,
    getInstanceCountryValidators,
    getLanguages,
    getMainLanguage,
    getPhonePrefix,
    getPrefixedAccountNumber,
    getSellZone,
    isGermanInstance,
    isRomaniaInstance
};
